import React, { useState } from 'react';
import axios from 'axios';
import { Mountain, CheckCircle, AlertCircle, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MiningConstructionContact = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    operation_type: '',
    location: '',
    equipment_list: '',
    production_capacity: '',
    permit_type_needed: '',
    fugitive_dust_concerns: '',
    rock_crusher_specs: '',
    asphalt_plant_specs: '',
    current_permit_status: '',
    project_phase: '',
    timeline: '',
    specific_requirements: '',
    additional_info: '',
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        subject: `Mining & Construction Inquiry - ${formData.operation_type}`,
        message: `
Company: ${formData.company}
Phone: ${formData.phone}
Operation Type: ${formData.operation_type}
Location: ${formData.location}
Equipment List: ${formData.equipment_list}
Production Capacity: ${formData.production_capacity}
Permit Type Needed: ${formData.permit_type_needed}
Fugitive Dust Concerns: ${formData.fugitive_dust_concerns}
Rock Crusher Specs: ${formData.rock_crusher_specs}
Asphalt Plant Specs: ${formData.asphalt_plant_specs}
Current Permit Status: ${formData.current_permit_status}
Project Phase: ${formData.project_phase}
Timeline: ${formData.timeline}
Specific Requirements or Concerns: ${formData.specific_requirements}
Additional Information: ${formData.additional_info}
        `.trim(),
        newsletter_signup: false,
      };

      const response = await axios.post(`${API}/contact`, payload);
      
      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: 'Thank you! We will review your mining/construction project details and contact you within 1-2 business days.',
        });
        setFormData({
          first_name: '',
          last_name: '',
          company: '',
          email: '',
          phone: '',
          operation_type: '',
          location: '',
          equipment_list: '',
          production_capacity: '',
          permit_type_needed: '',
          fugitive_dust_concerns: '',
          rock_crusher_specs: '',
          asphalt_plant_specs: '',
          current_permit_status: '',
          project_phase: '',
          timeline: '',
          specific_requirements: '',
          additional_info: '',
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'There was an error submitting your inquiry. Please try again or call us directly at (907) 687-8263.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-orange-900 to-orange-700 px-8 py-12 text-white">
            <div className="flex items-center mb-4">
              <Mountain className="w-12 h-12 mr-4" />
              <div>
                <h1 className="text-3xl font-bold">Mining & Road Construction Inquiry</h1>
                <p className="text-orange-100 mt-2">Tell us about your mining or construction project needs</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            {status.message && (
              <div className={`mb-6 p-4 rounded-lg flex items-start space-x-3 ${status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {status.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <p>{status.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                    <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name <span className="text-red-500">*</span></label>
                    <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company <span className="text-red-500">*</span></label>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone <span className="text-red-500">*</span></label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Operation Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Operation Type</label>
                    <select name="operation_type" value={formData.operation_type} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                      <option value="">Select operation type</option>
                      <option value="rock_crushing">Rock Crushing</option>
                      <option value="asphalt_production">Asphalt Production</option>
                      <option value="mining_operations">Mining Operations</option>
                      <option value="road_construction">Road Construction</option>
                      <option value="quarry">Quarry Operations</option>
                      <option value="gravel_pit">Gravel Pit</option>
                      <option value="multiple">Multiple Operations</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Location</label>
                    <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="City/Region in Alaska" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Equipment List</label>
                    <textarea name="equipment_list" value={formData.equipment_list} onChange={handleChange} rows="3" placeholder="List equipment: crushers, screens, conveyors, loaders, etc." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Production Capacity</label>
                    <input type="text" name="production_capacity" value={formData.production_capacity} onChange={handleChange} placeholder="e.g., 500 tons/hour, 200 tons/day" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permit Type Needed</label>
                    <select name="permit_type_needed" value={formData.permit_type_needed} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                      <option value="">Select permit type</option>
                      <option value="mg3_asphalt">MG3 - Asphalt Plant Permit</option>
                      <option value="mg9_rock_crusher">MG9 - Rock Crusher Permit (5+ tons/hr)</option>
                      <option value="minor_permit">Minor Source Permit</option>
                      <option value="title_v">Title V Operating Permit</option>
                      <option value="fugitive_dust">Fugitive Dust Mitigation Plan</option>
                      <option value="multiple_permits">Multiple Permits</option>
                      <option value="unsure">Not Sure - Need Consultation</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fugitive Dust Concerns</label>
                    <select name="fugitive_dust_concerns" value={formData.fugitive_dust_concerns} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                      <option value="">Select concern level</option>
                      <option value="significant">Significant - Need Comprehensive Plan</option>
                      <option value="moderate">Moderate - Standard Controls</option>
                      <option value="minimal">Minimal - Basic Mitigation</option>
                      <option value="haul_roads">Haul Road Dust Primary Concern</option>
                      <option value="stockpile">Stockpile Dust Issues</option>
                      <option value="crushing">Crushing/Screening Dust</option>
                      <option value="need_assessment">Need Dust Assessment</option>
                    </select>
                  </div>

                  {(formData.operation_type === 'rock_crushing' || formData.permit_type_needed === 'mg9_rock_crusher') && (
                    <div className="bg-orange-50 p-4 rounded-lg border-2 border-orange-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Rock Crusher Specifications</label>
                      <textarea name="rock_crusher_specs" value={formData.rock_crusher_specs} onChange={handleChange} rows="3" placeholder="Type of crusher, capacity (tons/hour), portable or stationary, etc." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"></textarea>
                    </div>
                  )}

                  {(formData.operation_type === 'asphalt_production' || formData.permit_type_needed === 'mg3_asphalt') && (
                    <div className="bg-orange-50 p-4 rounded-lg border-2 border-orange-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Asphalt Plant Specifications</label>
                      <textarea name="asphalt_plant_specs" value={formData.asphalt_plant_specs} onChange={handleChange} rows="3" placeholder="Plant type (batch/drum mix), production rate, portable or permanent, fuel type, etc." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"></textarea>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Permit Status</label>
                    <select name="current_permit_status" value={formData.current_permit_status} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                      <option value="">Select status</option>
                      <option value="no_permit">No Permit (New Operation)</option>
                      <option value="planning">Planning Phase</option>
                      <option value="existing">Have Existing Permit</option>
                      <option value="renewal">Permit Renewal Needed</option>
                      <option value="modification">Permit Modification</option>
                      <option value="compliance_issue">Compliance Issue</option>
                      <option value="expired">Expired Permit</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Phase</label>
                    <select name="project_phase" value={formData.project_phase} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                      <option value="">Select project phase</option>
                      <option value="initial_planning">Initial Planning</option>
                      <option value="equipment_purchase">Equipment Purchase Phase</option>
                      <option value="site_preparation">Site Preparation</option>
                      <option value="ready_to_operate">Ready to Operate</option>
                      <option value="currently_operating">Currently Operating</option>
                      <option value="expansion">Expanding Operations</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
                    <select name="timeline" value={formData.timeline} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                      <option value="">Select timeline</option>
                      <option value="immediate">Immediate (Within 2 weeks)</option>
                      <option value="1_month">Within 1 month</option>
                      <option value="construction_season">Before Construction Season</option>
                      <option value="3-6_months">3-6 months</option>
                      <option value="6+_months">6+ months</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specific Requirements or Concerns <span className="text-red-500">*</span></label>
                    <textarea name="specific_requirements" value={formData.specific_requirements} onChange={handleChange} required rows="4" placeholder="NSPS compliance needs, NESHAP requirements, portable operations, seasonal work, etc." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                    <textarea name="additional_info" value={formData.additional_info} onChange={handleChange} rows="3" placeholder="Any other details about your project..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"></textarea>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-orange-700 text-white py-4 px-6 rounded-lg font-semibold hover:bg-orange-800 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? <span>Submitting...</span> : <><span>Submit Mining/Construction Inquiry</span><Send size={20} /></>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiningConstructionContact;
